import { useEffect, useRef } from 'react';

export function useAgentWebSocket(sessionId: string | null, onNewMessage: (msg: any) => void) {
  const onNewMessageRef = useRef(onNewMessage);

  useEffect(() => {
    onNewMessageRef.current = onNewMessage;
  }, [onNewMessage]);

  useEffect(() => {
    if (!sessionId) return;
    
    // Connect directly to the Contact-CC Public WebSocket using its deployment token
    const token = process.env.NEXT_PUBLIC_CONTACT_CC_TOKEN;
    const baseWsUrl = process.env.NEXT_PUBLIC_CONTACT_CC_WS_URL;
    
    if (!token || !baseWsUrl) {
      console.error("[AgentWS] Missing required environment variables: NEXT_PUBLIC_CONTACT_CC_TOKEN or NEXT_PUBLIC_CONTACT_CC_WS_URL");
      return;
    }
    
    const wsUrl = `${baseWsUrl}?session_id=${encodeURIComponent(sessionId)}&token=${token}`;
    
    let ws: WebSocket | null = null;
    let pingInterval: NodeJS.Timeout;
    let reconnectTimeout: NodeJS.Timeout;
    
    const connect = () => {
      console.log("[AgentWS] Attempting to connect for session:", sessionId);
      
      try {
        ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          console.log("[AgentWS] ✓ Connected successfully");
          pingInterval = setInterval(() => {
            if (ws?.readyState === WebSocket.OPEN) ws.send('ping');
          }, 30000);
        };
        
        ws.onmessage = (event) => {
          if (event.data === 'pong') return;
          console.log("[AgentWS] Message received:", event.data);
          try {
            const data = JSON.parse(event.data);
            // Handle incoming messages from human agents
            if (data.type === 'new_message' && data.role === 'assistant') {
              // Update UI - pass the full data including session info for persistence
              onNewMessageRef.current({
                ...data,
                sessionId: sessionId  // Include session ID for later persistence
              });
            }
          } catch (e) {
            console.error("[AgentWS] Parse Error", e);
          }
        };
        
        ws.onclose = (event) => {
          console.log("[AgentWS] Disconnected.", event.code !== 1000 ? `Code: ${event.code}, Reason: ${event.reason || 'Unknown'}` : 'Normal closure');
          clearInterval(pingInterval);
          // Only reconnect if not a normal closure
          if (event.code !== 1000) {
            reconnectTimeout = setTimeout(connect, 5000);
          }
        };
        
        ws.onerror = () => {
          // WebSocket errors don't provide useful details in the event object
          console.warn("[AgentWS] Connection error occurred. This may be due to network issues or the backend service being unavailable.");
        };
      } catch (error) {
        console.error("[AgentWS] Failed to create WebSocket connection:", error);
      }
    };
    
    connect();
    
    return () => {
      if (ws) {
        ws.onclose = null; // Prevent auto-reconnect on unmount
        ws.close();
      }
      clearInterval(pingInterval);
      clearTimeout(reconnectTimeout);
    };
  }, [sessionId]);
}
